// ExportDialog — streamed, user-initiated export of one or more
// vault entries to a destination the user picks.
//
// Preferred path: File System Access API. Single file -> showSaveFilePicker;
// multiple files -> showDirectoryPicker + createWritable per entry.
// Bytes stream directly from the worker into the chosen writable,
// never touching a temp file or an in-memory buffer on the main
// thread (beyond the stream's transient chunks).
//
// Fallback: browsers without showSaveFilePicker / showDirectoryPicker
// (e.g., Firefox) receive a Blob URL the user must manually save to.
// This path buffers in memory and is guarded by an explicit warning
// banner, since it violates "streamed direct to destination" if the
// file is very large.

import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";
import { useVault } from "../../hooks/useVault";
import { useSession } from "../../stores/session";
import { makeTaskId } from "../../lib/uploadQueue";

// File System Access API — augment `window` so TS picks up the
// optional pickers in lib.dom that vary by release.
declare global {
  interface Window {
    showSaveFilePicker?: (options?: {
      suggestedName?: string;
      types?: Array<{
        description?: string;
        accept: Record<string, string[]>;
      }>;
    }) => Promise<FileSystemFileHandle>;
    showDirectoryPicker?: (options?: {
      mode?: "read" | "readwrite";
    }) => Promise<FileSystemDirectoryHandle>;
  }
  interface FileSystemDirectoryHandle {
    getFileHandle(
      name: string,
      options?: { create?: boolean },
    ): Promise<FileSystemFileHandle>;
  }
  interface FileSystemFileHandle {
    createWritable(options?: {
      keepExistingData?: boolean;
    }): Promise<FileSystemWritableFileStream>;
  }
  interface FileSystemWritableFileStream extends WritableStream<Uint8Array> {
    close(): Promise<void>;
  }
}

type Phase = "idle" | "running" | "done" | "error";

interface ExportDialogProps {
  folderPath: string;
  fileNames: string[];
  onClose: () => void;
}

function joinPath(folder: string, name: string): string {
  const base = folder.endsWith("/") ? folder : `${folder}/`;
  return `${base}${name}`.replace(/\/+/g, "/");
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KiB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MiB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GiB`;
}

export function ExportDialog({
  folderPath,
  fileNames,
  onClose,
}: ExportDialogProps) {
  const worker = useVault();
  const handle = useSession((s) => s.session?.handle ?? null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [currentFile, setCurrentFile] = useState<string | null>(null);
  const [bytesDone, setBytesDone] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);
  const [blobUrls, setBlobUrls] = useState<Array<{ name: string; url: string }>>(
    [],
  );
  const cancelledRef = useRef(false);

  const hasModernPickers = useMemo(
    () =>
      typeof window !== "undefined" &&
      typeof window.showSaveFilePicker === "function" &&
      typeof window.showDirectoryPicker === "function",
    [],
  );

  // Web Share API (mobile Chrome/Safari). Only usable for single-file
  // export; `canShare({ files: [probe] })` must be truthy because iOS
  // lies about `navigator.share` existing without file support.
  const canShareSingle = useMemo(() => {
    if (fileNames.length !== 1) return false;
    if (typeof navigator === "undefined" || !navigator.share) return false;
    if (typeof navigator.canShare !== "function") return false;
    try {
      const probe = new File([new Uint8Array()], fileNames[0]!, {
        type: "application/octet-stream",
      });
      return navigator.canShare({ files: [probe] });
    } catch {
      return false;
    }
  }, [fileNames]);

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      // Revoke any outstanding blob URLs on unmount.
      for (const { url } of blobUrls) URL.revokeObjectURL(url);
    };
    // We intentionally don't depend on blobUrls — cleanup happens
    // once on unmount using the latest ref via closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onStart = async () => {
    if (handle === null) {
      toast.error("Vault is locked.");
      return;
    }
    setPhase("running");
    try {
      if (hasModernPickers) {
        await exportWithPickers();
      } else {
        await exportWithBlobFallback();
      }
      setPhase("done");
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes("aborted") || msg.includes("AbortError")) {
        // User cancelled the native picker — dismiss silently.
        onClose();
        return;
      }
      toast.error(`Export failed: ${msg}`);
      setPhase("error");
    }
  };

  const onShareSingle = async () => {
    if (handle === null) {
      toast.error("Vault is locked.");
      return;
    }
    const name = fileNames[0];
    if (!name) return;
    setPhase("running");
    setCurrentFile(name);
    setBytesDone(0);
    setTotalBytes(0);
    try {
      const parts: Uint8Array[] = [];
      const sink = new WritableStream<Uint8Array>({
        write(chunk) {
          parts.push(chunk);
          setBytesDone((n) => n + chunk.byteLength);
        },
      });
      const taskId = makeTaskId();
      const srcPath = joinPath(folderPath, name);
      await worker.exportFile(handle, taskId, srcPath, sink);

      const file = new File(parts as BlobPart[], name, {
        type: "application/octet-stream",
      });
      // Release the transient chunks — the File owns the bytes now.
      parts.length = 0;
      await navigator.share({ files: [file] });
      setPhase("done");
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes("AbortError") || msg.includes("canceled")) {
        // User dismissed the share sheet.
        setPhase("idle");
        return;
      }
      toast.error(`Share failed: ${msg}`);
      setPhase("error");
    }
  };

  const exportWithPickers = async () => {
    if (handle === null) return;

    if (fileNames.length === 1) {
      const name = fileNames[0]!;
      const fileHandle = await window.showSaveFilePicker!({
        suggestedName: name,
      });
      const writable = await fileHandle.createWritable();
      await runOne(handle, name, writable);
    } else {
      const dirHandle = await window.showDirectoryPicker!({
        mode: "readwrite",
      });
      for (const name of fileNames) {
        if (cancelledRef.current) return;
        const fileHandle = await dirHandle.getFileHandle(name, {
          create: true,
        });
        const writable = await fileHandle.createWritable();
        await runOne(handle, name, writable);
      }
    }
  };

  const runOne = async (
    h: number,
    name: string,
    writable: FileSystemWritableFileStream,
  ) => {
    setCurrentFile(name);
    setBytesDone(0);
    setTotalBytes(0);
    const taskId = makeTaskId();
    const srcPath = joinPath(folderPath, name);

    // Wrap the writable so we can observe byte counts for progress
    // while still streaming straight to disk.
    const tap = new WritableStream<Uint8Array>({
      async write(chunk) {
        // Write first (backpressure), then update counters.
        const w = writable.getWriter();
        try {
          await w.write(chunk);
        } finally {
          w.releaseLock();
        }
        setBytesDone((n) => n + chunk.byteLength);
      },
      async close() {
        await writable.close();
      },
      async abort(reason) {
        await writable.abort(reason);
      },
    });

    await worker.exportFile(h, taskId, srcPath, tap);
  };

  const exportWithBlobFallback = async () => {
    if (handle === null) return;
    const out: Array<{ name: string; url: string }> = [];
    for (const name of fileNames) {
      if (cancelledRef.current) return;
      setCurrentFile(name);
      setBytesDone(0);
      setTotalBytes(0);

      const parts: Uint8Array[] = [];
      const sink = new WritableStream<Uint8Array>({
        write(chunk) {
          parts.push(chunk);
          setBytesDone((n) => n + chunk.byteLength);
        },
      });
      const taskId = makeTaskId();
      const srcPath = joinPath(folderPath, name);
      await worker.exportFile(handle, taskId, srcPath, sink);

      const blob = new Blob(parts as BlobPart[], {
        type: "application/octet-stream",
      });
      // Clear the in-memory buffer ASAP — the Blob now owns the bytes.
      parts.length = 0;
      const url = URL.createObjectURL(blob);
      out.push({ name, url });
    }
    setBlobUrls(out);
  };

  const running = phase === "running";
  const progressPct =
    totalBytes > 0
      ? Math.round((bytesDone / totalBytes) * 100)
      : running
        ? 0
        : phase === "done"
          ? 100
          : 0;

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !running) onClose();
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Export {fileNames.length} file{fileNames.length === 1 ? "" : "s"}
          </DialogTitle>
          <DialogDescription>
            Decrypted bytes stream directly to the destination you
            pick. The vault sees no temp file on disk.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-40 overflow-auto rounded-md border border-border-soft">
          <ul className="divide-y divide-border-soft">
            {fileNames.map((n) => (
              <li
                key={n}
                className="px-3 py-2 text-sm"
                title={n}
              >
                <span className="truncate font-medium text-text">{n}</span>
              </li>
            ))}
          </ul>
        </div>

        {!hasModernPickers && phase === "idle" && (
          <div className="rounded-md border border-warn/40 bg-warn/10 p-3 text-xs text-text">
            <p className="font-medium">Your browser lacks streaming save.</p>
            <p className="mt-1 text-text-soft">
              Files will be decrypted into a download link instead. Avoid
              exporting very large files this way.
            </p>
          </div>
        )}

        {phase !== "idle" && currentFile && (
          <div>
            <div className="flex items-center justify-between text-sm">
              <span className="truncate font-medium text-text">
                {currentFile}
              </span>
              <span className="text-xs tabular-nums text-text-dim">
                {formatBytes(bytesDone)}
                {totalBytes > 0 ? ` / ${formatBytes(totalBytes)}` : ""}
              </span>
            </div>
            <Progress value={progressPct} className="mt-1 h-1.5" />
          </div>
        )}

        {blobUrls.length > 0 && (
          // Copy/cut/drag/context-menu blockers here are defense-in-depth,
          // not a security boundary — DevTools, screen readers, and OS
          // screenshot tools trivially bypass them. The point is to make
          // the user's natural gesture to highlight-copy a filename or
          // "save link as" into a second location fail, so the only path
          // to the plaintext is the explicit download.
          <div
            data-testid="export-blob-links"
            className="select-none rounded-md border border-border-soft p-2"
            style={{ userSelect: "none", WebkitUserSelect: "none" }}
            onCopy={(e) => e.preventDefault()}
            onCut={(e) => e.preventDefault()}
            onContextMenu={(e) => e.preventDefault()}
            onDragStart={(e) => e.preventDefault()}
          >
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-soft">
              Download links
            </p>
            <ul className="space-y-1">
              {blobUrls.map(({ name, url }) => (
                <li key={name} className="text-sm">
                  <a
                    href={url}
                    download={name}
                    className="text-accent hover:underline"
                  >
                    {name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter>
          {phase === "idle" && (
            <>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              {canShareSingle && !hasModernPickers && (
                <Button
                  onClick={() => {
                    void onShareSingle();
                  }}
                  disabled={handle === null}
                >
                  Share…
                </Button>
              )}
              <Button
                onClick={() => {
                  void onStart();
                }}
                disabled={handle === null}
                variant={canShareSingle && !hasModernPickers ? "outline" : "default"}
              >
                {hasModernPickers ? "Choose destination…" : "Prepare downloads"}
              </Button>
            </>
          )}
          {running && (
            <Button variant="outline" disabled>
              Exporting…
            </Button>
          )}
          {(phase === "done" || phase === "error") && (
            <Button onClick={onClose}>Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
